import { useQuery, useMutation } from 'react-query';
import {
  getCasesData,
  getCaseDetails,
  getASRCaseDetails,
  updateAsrCasesDetails,
  sendAsrApplicationEx,
  getAllCases,
} from '../api/cases';
import { queryClient } from '../config/react-query';
import { Case, CaseDetails } from '__generated__/api-di';

async function getCases(commercialRelation: string | null): Promise<Case[]> {
  const casesList = await getCasesData(commercialRelation);
  return casesList;
}
interface Id {
  commercialLabel: string;
  loanNumberExternal: number;
  applicationIndexNumber: number;
}

export const getId = (application: Case): Id => ({
  commercialLabel: application.lender!.label!,
  loanNumberExternal: application.application!.loanNumberExternal!,
  applicationIndexNumber: application.application!.index!,
});

async function getApplicationDetailsEx(id: Id): Promise<CaseDetails> {
  const { commercialLabel, loanNumberExternal, applicationIndexNumber } = id;
  const applicationDetails = await getCaseDetails({
    commercialLabel,
    loanNumberExternal,
    applicationIndexNumber,
  });
  return applicationDetails;
}

async function getAsrDetailsEx(id: number): Promise<any> {
  const asrDetails = await getASRCaseDetails(id);
  return asrDetails;
}

export function useQueryAllCases() {
  return useQuery(['cases'], () => getAllCases());
}

export function useQueryCases() {
  const commercialRelation = localStorage.getItem('commercialRelation');

  return useQuery(['cases', commercialRelation], () => getCases(commercialRelation));
}

export function optimisticUpdateApplicationEx(
  commercialLabel: string,
  loanNumberExternal: number,
  applicationIndexNumber: number
) {
  const queryKey = ['cases'];
  queryClient.cancelQueries('cases');

  // mutate application
  const applications = queryClient.getQueryData<Case[]>(queryKey);
  const updatedApplication = applications?.find((application) => {
    return (
      application.lender?.label === commercialLabel &&
      application.application?.loanNumberExternal === loanNumberExternal &&
      application.application?.index === applicationIndexNumber
    );
  }) as any;
  const updatedReceivedProofItems = updatedApplication.numberOfReceivedProofItems + 1;
  updatedApplication.numberOfReceivedProofItems = updatedReceivedProofItems.toString();

  queryClient.setQueryData(queryKey, applications);
  queryClient.invalidateQueries(queryKey);
}

export function useQueryApplicationDetailsEx(id: Id, enabled: boolean = true) {
  return useQuery(['case-details', id], () => getApplicationDetailsEx(id), {
    enabled: enabled,
  });
}

export function useQueryAsrApplicationDetailsEx(id: number) {
  const { status, error, isError, isFetching, data } = useQuery(
    ['asr-details', id],
    () => getAsrDetailsEx(id),
    { enabled: !!id }
  );

  return {
    status,
    error,
    isError,
    isFetching,
    data,
  };
}

export function updateAsrApplicationEx(asrDetails: any) {
  return updateAsrCasesDetails(asrDetails);
}

export function useMutationAsrApplicationEx() {
  return useMutation((asrDetails: any) => sendAsrApplicationEx(asrDetails));
}
